<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template */
import { computed, onMounted, reactive, ref } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import * as XLSX from 'xlsx';
import { useAlert } from 'dashboard/composables';
import KefubaoPhrasesAPI from 'dashboard/api/kefubaoPhrases';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const IMPORT_COLUMNS = {
  title: ['标题', 'title'],
  category: ['分类', 'category'],
  keywords: ['关键词', 'keywords'],
  reply_zh: ['中文话术', '中文', 'reply_zh', 'zh'],
  reply_en: ['英语话术', '英语', 'reply_en', 'en'],
  reply_hi: ['印地语话术', '印地语', 'reply_hi', 'hi'],
};

const EXPORT_HEADERS = {
  title: '标题',
  category: '分类',
  keywords: '关键词',
  reply_zh: '中文话术',
  reply_en: '英语话术',
  reply_hi: '印地语话术',
};

const TEMPLATE_ROWS = [
  {
    标题: '充值不到账',
    分类: '充值问题',
    关键词: 'deposit, recharge, 充值, भुगतान',
    中文话术: '您好，请提供充值截图和交易ID，我们会尽快为您核实。',
    英语话术:
      'Hello, please provide your recharge screenshot and transaction ID. We will verify it as soon as possible.',
    印地语话术:
      'नमस्ते, कृपया अपना रिचार्ज स्क्रीनशॉट और ट्रांजैक्शन आईडी भेजें। हम जल्द से जल्द जांच करेंगे।',
  },
];

const emptyForm = () => ({
  id: null,
  title: '',
  category: '',
  keywords: '',
  reply_zh: '',
  reply_en: '',
  reply_hi: '',
});

const records = ref([]);
const searchQuery = ref('');
const selectedCategory = ref('');
const selectedKeyword = ref('');
const isFetching = ref(false);
const isSaving = ref(false);
const isImporting = ref(false);
const editingId = ref(null);
const importInput = ref(null);
const form = reactive(emptyForm());

const keywordList = keywords =>
  String(keywords || '')
    .split(/[,，、\s/|]+/)
    .map(keyword => keyword.trim())
    .filter(Boolean);

const buildFilterItems = (items, getValues) => {
  const counts = new Map();
  items.forEach(item => {
    getValues(item).forEach(value => {
      counts.set(value, (counts.get(value) || 0) + 1);
    });
  });
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

const categoryOptions = computed(() =>
  buildFilterItems(records.value, phrase => [phrase.category || '未分类'])
);

const keywordOptions = computed(() =>
  buildFilterItems(records.value, phrase => keywordList(phrase.keywords))
);

const filteredByFacet = computed(() =>
  records.value.filter(phrase => {
    const matchesCategory =
      !selectedCategory.value ||
      (phrase.category || '未分类') === selectedCategory.value;
    const matchesKeyword =
      !selectedKeyword.value ||
      keywordList(phrase.keywords).includes(selectedKeyword.value);
    return matchesCategory && matchesKeyword;
  })
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return filteredByFacet.value;
  return picoSearch(filteredByFacet.value, query, [
    { name: 'title', weight: 4 },
    { name: 'category', weight: 3 },
    'keywords',
    'reply_zh',
    'reply_en',
    'reply_hi',
  ]);
});

const resetForm = () => {
  Object.assign(form, emptyForm());
  editingId.value = null;
};

const clearFilters = () => {
  selectedCategory.value = '';
  selectedKeyword.value = '';
};

const loadPhrases = async () => {
  isFetching.value = true;
  try {
    const { data } = await KefubaoPhrasesAPI.get();
    records.value = data || [];
  } catch (error) {
    useAlert(error?.response?.data?.error || '客服话术加载失败');
  } finally {
    isFetching.value = false;
  }
};

const editPhrase = phrase => {
  Object.assign(form, {
    id: phrase.id,
    title: phrase.title || '',
    category: phrase.category || '',
    keywords: phrase.keywords || '',
    reply_zh: phrase.reply_zh || phrase.reply?.zh || '',
    reply_en: phrase.reply_en || phrase.reply?.en || '',
    reply_hi: phrase.reply_hi || phrase.reply?.hi || '',
  });
  editingId.value = phrase.id;
};

const savePhrase = async () => {
  if (!form.title.trim()) {
    useAlert('请填写话术标题');
    return;
  }
  if (!form.reply_zh.trim() || !form.reply_en.trim() || !form.reply_hi.trim()) {
    useAlert('中文、英语、印地语三条话术都必须填写');
    return;
  }

  isSaving.value = true;
  const payload = {
    title: form.title.trim(),
    category: form.category.trim(),
    keywords: form.keywords.trim(),
    reply_zh: form.reply_zh.trim(),
    reply_en: form.reply_en.trim(),
    reply_hi: form.reply_hi.trim(),
  };

  try {
    if (editingId.value) {
      const { data } = await KefubaoPhrasesAPI.update(editingId.value, payload);
      records.value = records.value.map(item =>
        item.id === editingId.value ? data : item
      );
      useAlert('客服话术已更新');
    } else {
      const { data } = await KefubaoPhrasesAPI.create(payload);
      records.value = [data, ...records.value];
      useAlert('客服话术已新增');
    }
    resetForm();
  } catch (error) {
    useAlert(error?.response?.data?.error || '保存失败');
  } finally {
    isSaving.value = false;
  }
};

const deletePhrase = async phrase => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`确定删除「${phrase.title}」吗？`)) return;

  try {
    await KefubaoPhrasesAPI.delete(phrase.id);
    records.value = records.value.filter(item => item.id !== phrase.id);
    if (editingId.value === phrase.id) resetForm();
    useAlert('客服话术已删除');
  } catch (error) {
    useAlert(error?.response?.data?.error || '删除失败');
  }
};

const formatDate = () => {
  const now = new Date();
  const pad = value => String(value).padStart(2, '0');
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
};

const autosizeWorksheet = (worksheet, rows) => {
  const headers = Object.values(EXPORT_HEADERS);
  worksheet['!cols'] = headers.map(header => {
    const maxLength = rows.reduce(
      (max, row) => Math.max(max, String(row[header] || '').length),
      header.length
    );
    return { wch: Math.min(Math.max(maxLength + 2, 12), 60) };
  });
};

const writeWorkbook = (rows, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  autosizeWorksheet(worksheet, rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '客服话术');
  XLSX.writeFile(workbook, filename);
};

const exportPhrases = () => {
  const rows = records.value.map(phrase => ({
    [EXPORT_HEADERS.title]: phrase.title || '',
    [EXPORT_HEADERS.category]: phrase.category || '',
    [EXPORT_HEADERS.keywords]: phrase.keywords || '',
    [EXPORT_HEADERS.reply_zh]: phrase.reply_zh || '',
    [EXPORT_HEADERS.reply_en]: phrase.reply_en || '',
    [EXPORT_HEADERS.reply_hi]: phrase.reply_hi || '',
  }));

  writeWorkbook(
    rows.length
      ? rows
      : [
          Object.fromEntries(
            Object.values(EXPORT_HEADERS).map(header => [header, ''])
          ),
        ],
    `客服话术_${formatDate()}.xlsx`
  );
};

const downloadImportTemplate = () => {
  writeWorkbook(TEMPLATE_ROWS, '客服话术导入模板.xlsx');
};

const triggerImport = () => {
  importInput.value?.click();
};

const readImportValue = (row, field) => {
  const matchedKey = IMPORT_COLUMNS[field].find(key =>
    Object.prototype.hasOwnProperty.call(row, key)
  );
  return String(row[matchedKey] || '').trim();
};

const normalizeImportRow = row => ({
  title: readImportValue(row, 'title'),
  category: readImportValue(row, 'category'),
  keywords: readImportValue(row, 'keywords'),
  reply_zh: readImportValue(row, 'reply_zh'),
  reply_en: readImportValue(row, 'reply_en'),
  reply_hi: readImportValue(row, 'reply_hi'),
});

const isValidImportRow = row =>
  row.title && row.reply_zh && row.reply_en && row.reply_hi;

const importPhrases = async event => {
  const [file] = event.target.files || [];
  if (!file) return;

  isImporting.value = true;
  try {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(firstSheet, {
      defval: '',
      raw: false,
    });
    const normalizedRows = rows.map(normalizeImportRow);
    const validRows = normalizedRows.filter(isValidImportRow);

    if (!validRows.length) {
      useAlert('未读取到有效话术，请确认表头和模板一致');
      return;
    }

    const createdRows = await Promise.all(
      validRows.map(row =>
        KefubaoPhrasesAPI.create(row).then(({ data }) => data)
      )
    );

    records.value = [...createdRows.reverse(), ...records.value];
    useAlert(
      `导入完成：成功 ${createdRows.length} 条，跳过 ${rows.length - validRows.length} 条`
    );
  } catch (error) {
    useAlert(error?.response?.data?.error || '导入失败，请检查文件格式');
  } finally {
    isImporting.value = false;
    event.target.value = '';
  }
};

onMounted(loadPhrases);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    loading-message="正在加载客服话术..."
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        title="客服话术"
        description="维护客服宝助手使用的话术库，每条话术包含中文、英语、印地语三种回复。"
        search-placeholder="搜索标题、分类、关键词或话术内容"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            显示 {{ filteredRecords.length }} / {{ records.length }} 条
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-2">
            <button
              class="h-8 rounded-md border border-n-weak px-3 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-black2"
              type="button"
              @click="downloadImportTemplate"
            >
              下载导入模板
            </button>
            <button
              class="h-8 rounded-md border border-n-weak px-3 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-black2"
              type="button"
              :disabled="isImporting"
              @click="triggerImport"
            >
              {{ isImporting ? '导入中...' : '导入XLSX' }}
            </button>
            <button
              class="h-8 rounded-md bg-n-brand px-3 text-xs font-medium text-white"
              type="button"
              @click="exportPhrases"
            >
              导出XLSX
            </button>
            <input
              ref="importInput"
              class="hidden"
              type="file"
              accept=".xlsx,.xls"
              @change="importPhrases"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="grid grid-cols-[340px_minmax(0,1fr)_360px] gap-5">
        <aside
          class="sticky top-4 flex max-h-[calc(100vh-8rem)] flex-col self-start rounded-lg border border-n-weak bg-n-solid-1 p-3"
        >
          <div class="mb-3 flex items-center justify-between">
            <div class="text-sm font-semibold text-n-slate-12">筛选</div>
            <button
              class="text-xs text-n-slate-11"
              type="button"
              @click="clearFilters"
            >
              清空
            </button>
          </div>

          <div class="mb-5">
            <div class="mb-2 text-xs font-medium text-n-slate-11">分类</div>
            <div
              class="kefubao-filter-scroll grid max-h-72 gap-1 overflow-y-auto pr-2"
            >
              <button
                class="flex min-h-8 items-center justify-between rounded-md border px-2 text-left text-xs"
                :class="
                  !selectedCategory
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-transparent text-n-slate-12 hover:bg-n-alpha-black2'
                "
                type="button"
                @click="selectedCategory = ''"
              >
                <span>全部分类</span>
                <span>{{ records.length }}</span>
              </button>
              <button
                v-for="category in categoryOptions"
                :key="category.name"
                class="flex min-h-8 items-center justify-between gap-2 rounded-md border px-2 py-1 text-left text-xs"
                :class="
                  selectedCategory === category.name
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-transparent text-n-slate-12 hover:bg-n-alpha-black2'
                "
                type="button"
                @click="selectedCategory = category.name"
              >
                <span class="min-w-0 truncate">{{ category.name }}</span>
                <span>{{ category.count }}</span>
              </button>
            </div>
          </div>

          <div>
            <div class="mb-2 text-xs font-medium text-n-slate-11">关键词</div>
            <div
              class="kefubao-filter-scroll flex max-h-[320px] flex-wrap gap-2 overflow-y-auto pr-2"
            >
              <button
                v-for="keyword in keywordOptions"
                :key="keyword.name"
                class="rounded-md border px-2 py-1 text-xs"
                :class="
                  selectedKeyword === keyword.name
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-weak text-n-slate-12 hover:bg-n-alpha-black2'
                "
                type="button"
                @click="
                  selectedKeyword =
                    selectedKeyword === keyword.name ? '' : keyword.name
                "
              >
                {{ keyword.name }} · {{ keyword.count }}
              </button>
            </div>
          </div>
        </aside>

        <div
          class="overflow-hidden rounded-lg border border-n-weak bg-n-solid-1"
        >
          <div
            v-for="phrase in filteredRecords"
            :key="phrase.id"
            class="border-b border-n-weak p-4 last:border-b-0"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="truncate text-sm font-semibold text-n-slate-12">
                  {{ phrase.title }}
                </div>
                <div class="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span
                    class="rounded bg-n-alpha-black2 px-2 py-0.5 text-n-slate-11"
                  >
                    {{ phrase.category || '未分类' }}
                  </span>
                  <span
                    v-if="phrase.keywords"
                    class="min-w-0 truncate text-n-slate-10"
                  >
                    {{ phrase.keywords }}
                  </span>
                </div>
              </div>
              <div class="flex shrink-0 gap-2">
                <button
                  class="h-8 rounded-md border border-n-weak px-3 text-xs text-n-slate-12"
                  type="button"
                  @click="editPhrase(phrase)"
                >
                  编辑
                </button>
                <button
                  class="h-8 rounded-md border border-n-ruby-5 px-3 text-xs text-n-ruby-11"
                  type="button"
                  @click="deletePhrase(phrase)"
                >
                  删除
                </button>
              </div>
            </div>

            <div class="mt-3 grid grid-cols-3 gap-2 text-sm text-n-slate-12">
              <div class="rounded-md bg-n-alpha-black2 p-2">
                <div class="mb-1 text-xs font-medium text-n-slate-11">中文</div>
                <div class="max-h-20 overflow-hidden whitespace-pre-wrap">
                  {{ phrase.reply_zh }}
                </div>
              </div>
              <div class="rounded-md bg-n-alpha-black2 p-2">
                <div class="mb-1 text-xs font-medium text-n-slate-11">英语</div>
                <div class="max-h-20 overflow-hidden whitespace-pre-wrap">
                  {{ phrase.reply_en }}
                </div>
              </div>
              <div class="rounded-md bg-n-alpha-black2 p-2">
                <div class="mb-1 text-xs font-medium text-n-slate-11">
                  印地语
                </div>
                <div class="max-h-20 overflow-hidden whitespace-pre-wrap">
                  {{ phrase.reply_hi }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="records.length && !filteredRecords.length"
            class="p-6 text-center text-sm text-n-slate-11"
          >
            没有找到匹配的话术。
          </div>
          <div
            v-if="!records.length"
            class="p-10 text-center text-sm text-n-slate-11"
          >
            暂无客服话术，请在右侧填写内容后点击“新增话术”。
          </div>
        </div>

        <form
          class="sticky top-4 max-h-[calc(100vh-8rem)] self-start overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 p-4"
          @submit.prevent="savePhrase"
        >
          <div class="mb-4 flex items-center justify-between">
            <div class="text-base font-semibold text-n-slate-12">
              {{ editingId ? '编辑话术' : '新增话术' }}
            </div>
            <button
              v-if="editingId"
              class="text-xs text-n-slate-11"
              type="button"
              @click="resetForm"
            >
              取消编辑
            </button>
          </div>

          <label class="mb-3 block text-sm text-n-slate-12">
            标题
            <input
              v-model="form.title"
              class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-3 text-sm"
              placeholder="例如：网络问题"
            />
          </label>

          <label class="mb-3 block text-sm text-n-slate-12">
            分类
            <input
              v-model="form.category"
              class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-3 text-sm"
              placeholder="例如：登录/充值/提现"
            />
          </label>

          <label class="mb-3 block text-sm text-n-slate-12">
            关键词
            <input
              v-model="form.keywords"
              class="mt-1 h-9 w-full rounded-md border border-n-weak bg-n-solid-2 px-3 text-sm"
              placeholder="network, help, समस्या"
            />
          </label>

          <label class="mb-3 block text-sm text-n-slate-12">
            中文话术
            <textarea
              v-model="form.reply_zh"
              class="mt-1 min-h-20 w-full rounded-md border border-n-weak bg-n-solid-2 p-3 text-sm"
            />
          </label>

          <label class="mb-3 block text-sm text-n-slate-12">
            英语话术
            <textarea
              v-model="form.reply_en"
              class="mt-1 min-h-20 w-full rounded-md border border-n-weak bg-n-solid-2 p-3 text-sm"
            />
          </label>

          <label class="mb-4 block text-sm text-n-slate-12">
            印地语话术
            <textarea
              v-model="form.reply_hi"
              class="mt-1 min-h-20 w-full rounded-md border border-n-weak bg-n-solid-2 p-3 text-sm"
            />
          </label>

          <button
            class="h-9 w-full rounded-md bg-n-brand px-3 text-sm font-medium text-white"
            type="submit"
            :disabled="isSaving"
          >
            {{ isSaving ? '保存中...' : editingId ? '保存修改' : '新增话术' }}
          </button>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>

<style scoped>
.kefubao-filter-scroll {
  scrollbar-width: auto;
  scrollbar-color: rgb(var(--slate-6)) transparent;
}

.kefubao-filter-scroll::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

.kefubao-filter-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.kefubao-filter-scroll::-webkit-scrollbar-thumb {
  background-color: rgb(var(--slate-6));
  border: 3px solid transparent;
  border-radius: 999px;
  background-clip: content-box;
}

.kefubao-filter-scroll::-webkit-scrollbar-thumb:hover {
  background-color: rgb(var(--slate-8));
}
</style>
